import React from 'react';
import { Link } from 'react-router-dom';
import { Bed, Home, MapPin, Phone, Mail, MessageCircle, Maximize } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PropertyType } from '@/types/property';
import { encodeS3Url } from '../encodeS3Url';
import { capitalizeWords } from '@/helper';

interface PropertyCardProps {
  type?: string;
  property: PropertyType;
  actions?: React.ReactNode;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  type,
  property,
  actions
}) => {

  const {
    pricing,
    specifications,
    media,
    management
  } = property;

  const image = encodeS3Url(media.images?.[0]);
  const price = pricing.amount;
  const title = capitalizeWords(specifications.titleDeed);
  const bedrooms = specifications.beds || "-";
  const propType = capitalizeWords(specifications.propertyType || "-");
  const area = specifications.size || "-";
  const status = capitalizeWords(management.availability);

  const formatPrice = (price: number) => {
    if (!price || price === 0) return '-';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const statusColor = {
    'Available': 'bg-green-100 text-green-800 hover:bg-green-100',
    'Not Available': 'bg-red-100 text-red-800 hover:bg-red-100',
    'Off Plan': 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    'Pending': 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    'Published': 'bg-green-100 text-green-800 hover:bg-green-100',
    'Rejected': 'bg-red-100 text-red-800 hover:bg-red-100',
    'Draft': 'bg-muted text-muted-foreground hover:bg-muted',
  };

  return (
    <div className="property-card group rounded-3xl overflow-hidden glass-card animate-fade-in relative flex flex-col h-full bg-card border border-border/40">

      {/* Full Link Overlay */}
      <Link
        to={`/properties/${property.slug}`}
        className="absolute inset-0 z-0 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-3xl"
        aria-label={`View details for ${title}`}
      />

      {/* Image Section with Padding "Frame" */}
      <div className="relative property-image-aspect-large px-2 pt-2 z-10 pointer-events-none">
        <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-sm bg-muted/20">
          {image ? (
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 ease-out will-change-transform transform-gpu group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
              <img
                src={"/placeholder.svg"}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-500 ease-out will-change-transform transform-gpu group-hover:scale-105"
                loading="lazy"
              />
            </div>
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>

          {/* Price and Location on Image */}
          <div className="absolute bottom-0 left-0 w-full p-4 z-20 flex flex-col justify-end h-full">
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2 text-white">
                <p className="text-xl md:text-2xl font-bold drop-shadow-md">
                  {formatPrice(price)}
                </p>
                <span className="text-sm font-medium text-white/90 drop-shadow-md">AED</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/90 text-sm drop-shadow-md">
                <MapPin className="h-3.5 w-3.5 text-gold" />
                <span className="font-medium truncate">{property.location?.locality
                  ? `${capitalizeWords(property.location.locality)}, Dubai`
                  : "Dubai"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Badges - Absolute to the padded wrapper */}
        <div className="absolute top-6 left-6 right-6 flex justify-between items-start z-30 pointer-events-auto">
          {type === "featured" && (
            <Badge className="bg-primary hover:bg-primary text-white shadow-sm border-0">Featured</Badge>
          )}
          {type === "trending" && (
            <Badge className="bg-primary hover:bg-primary text-white shadow-sm border-0">Trending</Badge>
          )}
          <Badge className={`${statusColor[status as keyof typeof statusColor] || 'bg-white text-gray-800'} ml-auto shadow-sm border-0`}>{status}</Badge>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5 flex flex-col flex-1 relative z-10 pointer-events-none">

        {/* Title */}
        <div className="pb-4">
          <h3 className="text-xl font-bold line-clamp-2 transition-colors duration-300 leading-tight text-foreground">
            {title}
          </h3>
        </div>

        {/* Specs Grid */}
        <div className="grid grid-cols-3 gap-2 border-t border-border/50 pt-4 mt-auto">
          <div className="flex flex-col items-center justify-center text-center gap-1 group/item">
            <div className="p-2 rounded-lg bg-secondary/70 group-hover/item:bg-gold/20 transition-colors">
              <Home className="h-4 w-4 text-foreground group-hover/item:text-gold transition-colors" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm">{propType}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Type</span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center gap-1 group/item border-l border-border/50">
            <div className="p-2 rounded-lg bg-secondary/70 group-hover/item:bg-gold/20 transition-colors">
              <Bed className="h-4 w-4 text-foreground group-hover/item:text-gold transition-colors" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm">{bedrooms}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{Number(bedrooms) === 1 ? 'Bed' : 'Beds'}</span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center gap-1 group/item border-l border-border/50">
            <div className="p-2 rounded-lg bg-secondary/70 group-hover/item:bg-gold/20 transition-colors">
              <Maximize className="h-4 w-4 text-foreground group-hover/item:text-gold transition-colors" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sm">{area}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sq.Ft</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 pt-4 border-t border-border/50 relative z-20 pointer-events-auto">
          {actions ? (
            actions
          ) : (
            <div className="flex gap-6">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 border-border/50 hover:border-border/50 hover:opacity-80 transition-opacity bg-background/50 backdrop-blur-sm"
                title="Call"
                asChild
              >
                <a href="tel:+971501234567" onClick={(e) => e.stopPropagation()}>
                  <Phone className="h-4 w-4" />
                  <span className="sr-only">Call</span>
                </a>
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 border-border/50 hover:border-border/50 hover:opacity-80 transition-opacity bg-background/50 backdrop-blur-sm"
                title="Email"
                asChild
              >
                <a href="mailto:info@urbanoasis.ae" onClick={(e) => e.stopPropagation()}>
                  <Mail className="h-4 w-4" />
                  <span className="sr-only">Email</span>
                </a>
              </Button>
              <Button
                className="flex-1 gap-2 bg-[#25D366] hover:bg-[#25D366] hover:opacity-90 text-white border-none shadow-sm transition-all hover:shadow-md"
                size="sm"
                asChild
              >
                <a href="https://wa.me/971501234567" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
